import * as React from 'react'

interface FloatingScrollbarState {
  visible: boolean
  left: number
  width: number
  scrollWidth: number
}

export function useFloatingScrollbar(scrollableRef: React.RefObject<HTMLElement | null>) {
  const floatBarRef = React.useRef<HTMLDivElement>(null)

  const [state, setState] = React.useState<FloatingScrollbarState>({
    visible: false,
    left: 0,
    width: 0,
    scrollWidth: 0,
  })

  const isUserOnFloat = React.useRef(false)

  React.useEffect(() => {
    const el = scrollableRef.current

    if (!el) return

    let rafId: number | null = null

    const update = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        const rect = el.getBoundingClientRect()
        const hasOverflow = el.scrollWidth > el.clientWidth
        const bottomBelowViewport = rect.bottom > window.innerHeight
        const isPartiallyVisible = rect.top < window.innerHeight && rect.bottom > 0

        setState((prev) => {
          const nextVisible = hasOverflow && bottomBelowViewport && isPartiallyVisible
          const nextLeft = rect.left
          const nextWidth = rect.width
          const nextScrollWidth = el.scrollWidth
          if (
            prev.visible === nextVisible &&
            prev.left === nextLeft &&
            prev.width === nextWidth &&
            prev.scrollWidth === nextScrollWidth
          ) {
            return prev
          }
          return { visible: nextVisible, left: nextLeft, width: nextWidth, scrollWidth: nextScrollWidth }
        })
      })
    }

    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)

    const ro = new ResizeObserver(update)
    ro.observe(el)

    update()

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      ro.disconnect()
    }
  }, [scrollableRef])

  React.useEffect(() => {
    const el = scrollableRef.current
    const floatEl = floatBarRef.current

    if (!el || !floatEl) return

    // Safari fires a scroll event even from programmatic scrollLeft assignment,
    // which causes infinite sync loops with flag-based approaches.
    // Instead, track which element the user is physically interacting with:
    // only propagate scroll from the element the user is hovering/touching.
    const onMainScroll = () => {
      if (isUserOnFloat.current) return
      floatEl.scrollLeft = el.scrollLeft
    }

    const onFloatScroll = () => {
      if (!isUserOnFloat.current) return
      el.scrollLeft = floatEl.scrollLeft
    }

    const onFloatEnter = () => { isUserOnFloat.current = true }
    const onFloatLeave = () => { isUserOnFloat.current = false }
    const onFloatTouchStart = () => { isUserOnFloat.current = true }
    const onFloatTouchEnd = () => { isUserOnFloat.current = false }

    el.addEventListener('scroll', onMainScroll)
    floatEl.addEventListener('scroll', onFloatScroll)
    floatEl.addEventListener('mouseenter', onFloatEnter)
    floatEl.addEventListener('mouseleave', onFloatLeave)
    floatEl.addEventListener('touchstart', onFloatTouchStart)
    floatEl.addEventListener('touchend', onFloatTouchEnd)

    return () => {
      el.removeEventListener('scroll', onMainScroll)
      floatEl.removeEventListener('scroll', onFloatScroll)
      floatEl.removeEventListener('mouseenter', onFloatEnter)
      floatEl.removeEventListener('mouseleave', onFloatLeave)
      floatEl.removeEventListener('touchstart', onFloatTouchStart)
      floatEl.removeEventListener('touchend', onFloatTouchEnd)
    }
  }, [scrollableRef, state.visible])

  return { floatBarRef, ...state }
}
